/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { flowRight } from 'lodash';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import fullStory from 'js/lib/fullStoryUtils';
import { getCmsLocaleString } from 'js/lib/language';
import mapProps from 'js/lib/mapProps';
import pendo, { LEARNER_API_KEY } from 'js/lib/pendo';
import Retracked from 'js/lib/retracked';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';
import waitFor from 'js/lib/waitFor';

import type { Theme } from '@coursera/cds-core';

import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import type {
  DegreeHomePageContentQuery,
  DegreeHomePageContentQueryVariables,
} from 'bundles/degree-home/__generated__/contentfulTypes';
import DegreeHeader from 'bundles/degree-home/components/DegreeHeader';
import DegreeHeaderCms from 'bundles/degree-home/components/DegreeHeaderCms';
import DegreeRedirect from 'bundles/degree-home/components/DegreeRedirect';
import IntroModalWrapper from 'bundles/degree-home/components/IntroModalWrapper';
import TabsContainer from 'bundles/degree-home/components/TabsContainer';
import type { DegreesV1, LinearGradient2 } from 'bundles/degree-home/components/__generated__/DegreesV1';
import CoursesContextProvider from 'bundles/degree-home/components/v2/CoursesProvider';
import type { Partner } from 'bundles/degree-home/types/Degree';
import type { PageContent } from 'bundles/degree-home/types/contentfulTypes';
import {
  convertPremiumCertificatePageContentToGenericPageContent,
  convertUniversityProgramPageContentToGenericPageContent,
} from 'bundles/degree-home/types/contentfulTypes';
import { degreeHomePageContentQuery } from 'bundles/degree-home/utils/DegreeHomeAppContentfulQuery';
import { isFullStoryEnabled } from 'bundles/degree-home/utils/experimentUtils';
import type { LoadedMembershipStatus } from 'bundles/degree-home/utils/useDegreeMembershipStatus';
import withNaptimeAsProp from 'bundles/degree-home/utils/withNaptimeAsProp';
import type { UserAgent } from 'bundles/item-lti/constants/types';
import withCCPA from 'bundles/marketing-consent/components/withCCPA';
import withGDPR from 'bundles/marketing-consent/components/withGDPR';
import type Naptime from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import PageHeader from 'bundles/page-header/components/PageHeader';
import TimezoneMismatchNotification from 'bundles/page/components/TimezoneMismatchNotification';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import SimpleCourseraMetatags from 'bundles/seo/components/SimpleCourseraMetatags';

import _t from 'i18n!nls/degree-home';

import 'css!./__styl__/DegreeHomeApp';

const styles = {
  degreeHomePage: (theme: Theme) => css`
    /* need this to pass color contrast accessibility check on the page header */
    background-color: var(--cds-color-white-0);
  `,
  container: css`
    background-color: #f5f5f5; /* keep gray background for degree v1 */
    min-height: 100vh;
  `,
};

type InputProps = {
  userId: string;
  location: InjectedRouter['location'];
  degreeSlug: string;
  degreeMetadata: DegreesV1 | null | undefined;
  membershipStatus: LoadedMembershipStatus;
  partners: Array<Partner>;
};

type PropsFromStore = {
  userAgent: UserAgent;
};

export type Props = InputProps &
  Naptime.AddedProps &
  PropsFromStore & {
    children: JSX.Element;
    metatagTitle: string;
    degreeMetadata: DegreesV1;
    pageContent?: PageContent | null;
  };

export class DegreeHomeApp extends React.Component<Props> {
  componentDidMount() {
    const { naptime, userId, membershipStatus, degreeSlug, degreeMetadata } = this.props;

    if (membershipStatus === 'not_member') {
      redirect.setLocation(`/degrees/${degreeSlug}`);
    } else {
      naptime.executeMutation(
        ProgramSwitcherSelectionsV1.update(userId, {
          selectionType: 'DEGREE',
          degreeId: degreeMetadata.id,
        })
      );
    }

    const { external_id: externalId, is_staff: isStaff, is_superuser: isSuperuser } = user.get();

    // Init FullStory
    if (isFullStoryEnabled()) {
      fullStory.init();
      fullStory.set({
        userId: externalId,
        isStaff,
        isSuperuser,
        id: degreeMetadata?.id,
        name: degreeMetadata?.name,
        slug: degreeSlug,
      });
    }

    // Init Pendo
    pendo.init(
      {
        visitor: {
          id: externalId,
          isStaff,
          isSuperuser,
        },

        account: {
          id: degreeMetadata?.id,
          name: degreeMetadata?.name,
          slug: degreeSlug,
          internalLaunchType: degreeMetadata?.internalLaunchType,
          productVariant: degreeMetadata?.productVariant,
        },
      },
      LEARNER_API_KEY
    );
  }

  componentWillUnmount() {
    if (isFullStoryEnabled()) {
      fullStory.endSession();
    }
  }

  renderDegreeHeader() {
    const {
      degreeMetadata: { headerBackgroundColor, headerImage, name },
      pageContent,
      partners,
      userAgent,
    } = this.props;
    if (
      pageContent?.name &&
      pageContent?.partnerInformation?.primaryColor &&
      pageContent?.partnerInformation?.secondaryColor &&
      pageContent?.partnerInformation?.partnerName &&
      pageContent?.partnerInformation?.partnerLogo?.url &&
      pageContent?.partnerInformation?.bannerImage?.url
    ) {
      // Use Contentful data if _all_ of the necessary Contentful fields are defined
      return (
        <DegreeHeaderCms
          degreeName={pageContent.name}
          partnerInfo={{
            rectangularLogo: pageContent.partnerInformation.partnerLogo.url,
            altText: `${pageContent.partnerInformation.partnerName} ${_t('Logo')}`,
          }}
          fadeOnScroll={true}
          gradientColors={[pageContent.partnerInformation.primaryColor, pageContent.partnerInformation.secondaryColor]}
          bannerImage={pageContent.partnerInformation.bannerImage.url}
          isDegreeHome
          userAgent={userAgent}
        />
      );
    } else {
      const firstPartner = partners[0] || { rectangularLogo: '', name: '' };
      let gradientColors: string[] = [];
      if (headerBackgroundColor.typeName === 'linearGradient2') {
        const lg2 = headerBackgroundColor.definition as LinearGradient2;
        gradientColors = [
          `rgb(${lg2.color1.red},${lg2.color1.green},${lg2.color1.blue})`,
          `rgb(${lg2.color2.red},${lg2.color2.green},${lg2.color2.blue})`,
        ];
      }
      return (
        <DegreeHeader
          headerImageUrl={headerImage as string}
          degreeName={name}
          partner={firstPartner}
          gradientColors={gradientColors}
          userAgent={userAgent}
        />
      );
    }
  }

  render() {
    const { membershipStatus } = this.props;

    if (membershipStatus !== 'member') {
      return null;
    }

    const {
      children,
      degreeMetadata: { id, supportEmailAddress },
      metatagTitle,
    } = this.props;
    const isMasterTrack = stringKeyToTuple(id)[0] === 'credentialTrack';

    return (
      <CoursesContextProvider degreeId={id}>
        <TrackedDiv
          trackingName="degree_home_app"
          className="rc-DegreeHomeApp"
          withVisibilityTracking
          css={styles.degreeHomePage}
        >
          <SimpleCourseraMetatags title={metatagTitle} />
          <PageHeader hideSearch hideEnterprise showGDPRBanner logoWrapper="div" />

          {this.renderDegreeHeader()}

          <TimezoneMismatchNotification />

          <div css={styles.container}>
            <TabsContainer>
              {children &&
                React.cloneElement(children, {
                  supportEmailAddress,
                  ...this.props,
                })}
            </TabsContainer>
          </div>

          <IntroModalWrapper isMasterTrack={isMasterTrack} />
        </TrackedDiv>
      </CoursesContextProvider>
    );
  }
}

export default flowRight(
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'degree_home',
        page: 'degree_home_page',
      },
    };
  }),
  // If the user is a member of this degree and degreeId is not defined, render 404.
  // If the user is not a member of this degree, allow render of this component to redirect them to DDP.
  waitFor<InputProps>(
    ({ membershipStatus, degreeMetadata }) =>
      membershipStatus === 'not_member' || (degreeMetadata && degreeMetadata.id),
    <DegreeRedirect />
  ),
  mapProps<{ metatagTitle: string }, { location: { pathname: string }; degreeMetadata: { name: string } }>((props) => {
    const { location, degreeMetadata } = props;

    const name = degreeMetadata?.name;

    const isSummary = location.pathname.indexOf('summary') !== -1;
    let metatagTitle = `${name} | Home`;

    if (isSummary) {
      metatagTitle = `${name} | Summary`;
    }

    return { metatagTitle };
  }),
  connectToRouter(({ location }) => {
    const previewParam = location.query.preview;
    const isPreview = !!previewParam && Number(previewParam) === 1;

    return {
      isPreview,
    };
  }),
  connectToStores(['ApplicationStore'], ({ ApplicationStore }) => {
    return {
      userAgent: ApplicationStore.getUserAgent(),
    };
  }),
  graphql<
    { isPreview: boolean; degreeSlug: string },
    DegreeHomePageContentQuery,
    DegreeHomePageContentQueryVariables,
    { pageContent?: PageContent | null }
  >(degreeHomePageContentQuery, {
    options: ({ isPreview, degreeSlug }) => {
      return {
        variables: {
          preview: !!isPreview,
          slug: degreeSlug,
          locale: getCmsLocaleString(),
        },
        context: getGrowthCmsGraphqlContext(isPreview),
      };
    },
    props: ({ data }) => {
      const universityProgramPageContent = data?.universityProgramPageContentCollection?.items[0];
      const premiumCertificatePageContent = data?.premiumCertificatePageContentCollection?.items[0];
      return {
        pageContent:
          (universityProgramPageContent &&
            convertUniversityProgramPageContentToGenericPageContent(universityProgramPageContent)) ||
          (premiumCertificatePageContent &&
            convertPremiumCertificatePageContentToGenericPageContent(premiumCertificatePageContent)),
      };
    },
  }),
  withNaptimeAsProp,
  withGDPR,
  withCCPA
)(DegreeHomeApp);
